import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { getMe } from '@platform/client';
import { useQuery } from '@tanstack/react-query';

/**
 * Who the API thinks the caller is.
 *
 * This is the first authenticated call the admin makes, and it does two jobs beyond
 * showing a name. It is what provisions the account row — `GET /me` is the only
 * place that happens (spec 008 §6.1) — and it is the end-to-end check that the token
 * getter is wired: the SDK asks the auth layer for a token, attaches it, and the
 * API's guard accepts it. A misconfigured getter shows up here as a 401 rather than
 * as a mystery three screens later.
 */
export function WhoAmI() {
  const { data, error, isPending } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const response = await getMe({ throwOnError: true });
      return response.data;
    },
  });

  if (isPending) {
    return (
      <Typography variant="body2" color="text.secondary">
        Asking the API who you are…
      </Typography>
    );
  }

  if (error) {
    return <Alert severity="error">The API refused: {error.message}</Alert>;
  }

  return (
    <Alert severity="success">
      <Typography
        component="pre"
        variant="body2"
        sx={{ m: 0, fontFamily: 'monospace' }}
      >
        {`GET /me
id:      ${data.id}
roles:   ${data.roles.join(', ')}
created: ${data.createdAt}`}
      </Typography>
    </Alert>
  );
}
